<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { PermissionOrganization } from '@/generated/graphql'
import { computed, watch } from 'vue'
import useAuthorization from '@/composables/useAuthorization'

const { t } = useI18n()
const { permissionHierarchy } = useAuthorization()

const model = defineModel<PermissionOrganization[]>({ required: true })

type InheritanceReasons = { [key in PermissionOrganization]?: PermissionOrganization[] }
const inheritedPermissions = computed<InheritanceReasons>(() => {
  const ret: InheritanceReasons = {}
  model.value.forEach((selected) => {
    permissionHierarchy.value[selected]
      ?.filter((reachable) => reachable != selected)
      .forEach((reachable) => {
        if (ret[reachable]) {
          ret[reachable].push(selected)
        } else {
          ret[reachable] = [selected]
        }
      })
  })
  return ret
})
watch([model, inheritedPermissions], () => {
  Object.keys(inheritedPermissions.value).forEach((inherited) => {
    if (model.value.includes(inherited as PermissionOrganization)) {
      model.value = model.value.filter((selected) => selected != inherited)
    }
  })
})

const permissions = computed(() =>
  Object.values(PermissionOrganization).map((p) => {
    const inheritsFrom = (inheritedPermissions.value[p] || []).map((i) => t(`permission.${i}.name`))
    return {
      name: t(`permission.${p}.name`),
      description: t(`permission.${p}.description`),
      value: p,
      inheritsFrom,
    }
  }),
)
</script>

<template>
  <v-list v-model:selected="model" select-strategy="leaf">
    <v-list-item
      v-for="p in permissions"
      :key="p.value"
      density="compact"
      :value="p.value"
      :disabled="p.inheritsFrom.length > 0"
    >
      <template #prepend="{ isSelected, select }">
        <v-list-item-action start>
          <v-checkbox-btn
            :model-value="isSelected"
            density="compact"
            hide-details
            :indeterminate="p.inheritsFrom.length > 0"
            :disabled="p.inheritsFrom.length > 0"
            @update:model-value="select"
          />
        </v-list-item-action>
      </template>
      <v-list-item-title class="text-wrap">
        <div class="permission-name">
          {{ p.name
          }}<span v-if="p.inheritsFrom.length > 0" class="inheritance-text">
            - {{ t('component.organizationPermissionsPicker.inheritanceExplanation') }}
            {{ p.inheritsFrom.join(', ') }}
          </span>
        </div>
        <div class="permission-description">
          {{ p.description }}
        </div>
      </v-list-item-title>
    </v-list-item>
  </v-list>
</template>

<style scoped lang="scss">
.permission-name {
  font-weight: bold;
}

.inheritance-text {
  font-size: 0.8em;
  font-style: italic;
  font-weight: normal;
}

.permission-description {
  font-size: 0.85em;
  color: #666;
}
</style>
